import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MessageSquare,
  X,
  Send,
  Loader2,
  Bot,
  User,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { useSalesAgent, type LeadResult, type ChatMessage } from '@/hooks/useSalesAgent'
import { cn } from '@/lib/utils'

const EXAMPLE_QUESTIONS = [
  'PValve alabilecek tanker üreticileri',
  'Yangın kapısı ihtiyacı olan tersaneler',
  'Hollanda\'daki en büyük 10 tersane',
  'LNG/LPG gemisi yapan firmalar',
]

function formatRevenue(revenue: number | null): string {
  if (!revenue) return '-'
  if (revenue >= 1_000_000_000) return `$${(revenue / 1_000_000_000).toFixed(1)}B`
  if (revenue >= 1_000_000) return `$${(revenue / 1_000_000).toFixed(0)}M`
  if (revenue >= 1_000) return `$${(revenue / 1_000).toFixed(0)}K`
  return `$${revenue}`
}

function ResultsTable({ results, onViewLead }: { results: LeadResult[]; onViewLead: (id: string) => void }) {
  const [isExpanded, setIsExpanded] = useState(true)

  if (results.length === 0) {
    return (
      <div className="text-sm text-secondary-500 italic mt-2">
        Sonuç bulunamadı.
      </div>
    )
  }

  return (
    <div className="mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1 text-sm text-secondary-600 hover:text-secondary-900 mb-2"
      >
        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {results.length} sonuç
      </button>

      {isExpanded && (
        <div className="space-y-1 max-h-[300px] overflow-y-auto">
          {results.slice(0, 20).map((lead) => (
            <button
              key={lead.id}
              onClick={() => onViewLead(lead.id)}
              className="w-full text-left p-2 rounded-lg border border-secondary-200 bg-white hover:bg-primary-50 hover:border-primary-300 transition-colors"
            >
              <div className="font-medium text-primary-600 text-sm truncate">
                {lead.name}
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-secondary-500">
                <span>{lead.country}{lead.city && `, ${lead.city}`}</span>
                {lead.type && (
                  <span className={cn(
                    'px-1.5 py-0.5 rounded text-[10px] font-medium',
                    lead.type === 'repair' && 'bg-blue-100 text-blue-700',
                    lead.type === 'newbuild' && 'bg-green-100 text-green-700',
                    lead.type === 'scrapping' && 'bg-orange-100 text-orange-700'
                  )}>
                    {lead.type}
                  </span>
                )}
                {lead.employees && <span>{lead.employees.toLocaleString()} çalışan</span>}
                {lead.revenue && <span className="font-medium">{formatRevenue(lead.revenue)}</span>}
              </div>
            </button>
          ))}
          {results.length > 20 && (
            <div className="text-xs text-secondary-500 text-center py-2">
              +{results.length - 20} daha fazla sonuç
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function MessageBubble({ message, onViewLead }: { message: ChatMessage; onViewLead: (id: string) => void }) {
  const isUser = message.type === 'user'

  return (
    <div className={cn('flex gap-2', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
          <Bot className="w-4 h-4 text-primary-600" />
        </div>
      )}

      <div className={cn(
        'max-w-[85%] rounded-lg px-4 py-2',
        isUser
          ? 'bg-primary-600 text-white'
          : 'bg-white border border-secondary-200'
      )}>
        {message.isLoading ? (
          <div className="flex items-center gap-2 text-secondary-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Düşünüyorum...</span>
          </div>
        ) : (
          <>
            <p className={cn('text-sm', isUser ? 'text-white' : 'text-secondary-700')}>
              {message.content}
            </p>
            {message.response?.results && (
              <ResultsTable results={message.response.results} onViewLead={onViewLead} />
            )}
            {message.response?.confidence && (
              <div className="mt-2 flex items-center gap-2">
                <span className={cn(
                  'text-xs px-2 py-0.5 rounded-full',
                  message.response.confidence === 'high' && 'bg-green-100 text-green-700',
                  message.response.confidence === 'medium' && 'bg-yellow-100 text-yellow-700',
                  message.response.confidence === 'low' && 'bg-red-100 text-red-700'
                )}>
                  {message.response.confidence === 'high' && 'Yüksek güven'}
                  {message.response.confidence === 'medium' && 'Orta güven'}
                  {message.response.confidence === 'low' && 'Düşük güven'}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {isUser && (
        <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-secondary-600" />
        </div>
      )}
    </div>
  )
}

export function SalesAgentChat() {
  const [isOpen, setIsOpen] = useState(false)
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  const { messages, isLoading, askQuestion, clearMessages } = useSalesAgent()

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return

    const question = input.trim()
    setInput('')
    await askQuestion(question)
  }

  const handleExampleClick = async (question: string) => {
    setInput('')
    await askQuestion(question)
  }

  const handleViewLead = (id: string) => {
    navigate(`/admin/leads/${id}`)
    setIsOpen(false)
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 transition-all duration-200 flex items-center justify-center',
          isOpen && 'scale-0 opacity-0'
        )}
      >
        <Sparkles className="w-6 h-6" />
      </button>

      {/* Chat Panel */}
      <div
        className={cn(
          'fixed bottom-6 right-6 z-[9999] w-[440px] h-[600px] bg-secondary-50 rounded-2xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden',
          isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
        )}
      >
        {/* Header */}
        <div className="bg-primary-600 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5" />
            <div>
              <h3 className="font-semibold">Sales AI Assistant</h3>
              <p className="text-xs text-primary-200">Lead veritabanını sorgula</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={clearMessages}
                className="p-2 hover:bg-primary-500 rounded-lg transition-colors"
                title="Sohbeti temizle"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-primary-500 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <MessageSquare className="w-8 h-8 text-primary-600" />
              </div>
              <h4 className="font-medium text-secondary-900 mb-2">
                Lead Veritabanı Asistanı
              </h4>
              <p className="text-sm text-secondary-500 mb-6">
                Doğal dilde sorular sorarak 11,000+ firmayı sorgulayabilirsin.
              </p>
              <div className="w-full space-y-2">
                <p className="text-xs text-secondary-400 mb-2">Örnek sorular:</p>
                {EXAMPLE_QUESTIONS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleExampleClick(q)}
                    disabled={isLoading}
                    className="w-full text-left px-3 py-2 text-sm bg-white border border-secondary-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors disabled:opacity-50"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} onViewLead={handleViewLead} />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-4 bg-white border-t border-secondary-200">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Firmalar hakkında soru sor..."
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-secondary-50"
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
